import { AdFoxBanner, IAdFoxBannerContext } from '@cian/adfox';
import { TAdFoxBannerSizeProps } from '@cian/adfox-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { sendLogStatsBanner } from '../../../api/banners';
import { AdFoxBannerSection } from '../../../components/AdFoxBannerSection';
import { AdfoxBanner as AdFoxBannerNew } from '../../../components/AdfoxBanner';
import { StretchWithTGB } from '../../../components/adfox/templates/StretchWithTGB/StretchWithTGB';
import { MortgageInRentContainer } from '../../../components/adfox/templates/mortgage_in_rent';
import { OfferCardImageBannerContainer } from '../../../components/adfox/templates/offer_card_image_banner';
import { TgbBanner } from '../../../components/adfox/templates/tgb';
import { selectIsAgent } from '../../../selectors';
import { IAdFoxBannerTypeParams, TAdFoxBanner, TAdfoxBannerTemplate } from '../../../types/adfox';
import { useApplicationContext } from '../../ApplicationContext';
import { useAdfoxBannerActions } from '../hooks/useAdfoxBannerActions';
import { useAdfoxParamsLegacy } from '../hooks/useAdfoxParamsLegacy';

export type TAdfoxBannerOwnProps = {
  type: TAdFoxBanner;
  randomId: string;
  positionInfo: string;
  className?: string;
  additionalPuids?: IAdFoxBannerTypeParams;
  isNew?: boolean;
  hideable?: boolean;
  onLoad?: () => void;
  onClick?: () => void;
};
export type TAdfoxContainerProps = TAdfoxBannerOwnProps & TAdFoxBannerSizeProps;

export function AdfoxContainer({
  type,
  isNew,
  randomId,
  additionalPuids,
  onLoad: onBannerLoad,
  onClick: onBannerClick,
  positionInfo,
  className,
  hideable,
  ...sizeParams
}: TAdfoxContainerProps) {
  const {
    custom: { makeRequest },
  } = useApplicationContext();

  const isAgent = useSelector(selectIsAgent);

  const adfoxParams = useAdfoxParamsLegacy(type, randomId, additionalPuids);

  const { onClick, onLoad } = useAdfoxBannerActions({ onBannerLoad, onBannerClick });

  const component = React.useMemo(() => {
    if (!adfoxParams) {
      return null;
    }

    if (isNew) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { pr, pr1, ...rest } = adfoxParams;

      return (
        <AdFoxBannerNew
          ownerId={202100}
          disableYan={isAgent}
          lazyLoad={{
            fetchMargin: 0.5,
            mobileScaling: 1,
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          params={rest as any}
          hideable={hideable}
          onLoad={data => onLoad(data.bundleParams.bannerId)}
          {...sizeParams}
        />
      );
    }

    return (
      <AdFoxBanner
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        middleware={() => Promise.resolve(adfoxParams as any)}
        renderFetched={(context: IAdFoxBannerContext) => {
          onLoad(context.analyticsBannerId);

          switch (context['type'] as TAdfoxBannerTemplate) {
            case 'seo':
              return <TgbBanner context={context} />;
            case 'stretch':
              return <StretchWithTGB context={context} />;
            case 'offer_card_image_banner':
              return <OfferCardImageBannerContainer context={context} />;
            case 'mortgage_in_rent':
              return <MortgageInRentContainer context={context} />;
            default:
              return <div />;
          }
        }}
        renderFailure={error => {
          let errorType = 'template-error';

          if (error.message === 'Impossible to load script') {
            errorType = !window._cianCanRunAds ? 'request-error' : 'adblock-error';
          }

          sendLogStatsBanner(makeRequest, errorType, positionInfo);

          return <div />;
        }}
      />
    );
  }, [adfoxParams, isAgent, isNew, makeRequest, onLoad, positionInfo, sizeParams]);

  if (!adfoxParams) {
    return <div />;
  }

  return (
    <AdFoxBannerSection onClick={onClick} randomId={randomId} className={className} allowYandexAdsNetwork={!isAgent}>
      {component}
    </AdFoxBannerSection>
  );
}
